// @ts-nocheck
import {
  PASSIVE_TREES,
  SPELLS,
  spellStatsForTip,
  keyLabelForSlot,
  nextFreeSlot,
  shopCategoryLabel,
  passiveRequiresLabel,
  spellDescShort,
  synergiesHostedBy,
  synergyPartnerMet,
} from "../data";
import { spellIconInner, synergyOrbInner } from "../spell-icons";
import { Sound } from "../audio";
import { escapeHtml } from "../net-ui";
import { Game } from "./game-base";
import { DRAFT_PICK_UNLOCK_TIMING, runPickUnlockAnim } from "./pick-unlock-anim";

/** Synergy passives on current kit that picking spellId would newly enable. */
function synergiesUnlockedBySpell(spellId, loadout, committed) {
  const out = [];
  const seen = new Set();
  for (const hostId of Object.values(loadout || {})) {
    if (!hostId) continue;
    for (const n of synergiesHostedBy(hostId)) {
      if (n.requiresSpell !== spellId || seen.has(n.id)) continue;
      if (synergyPartnerMet(loadout, committed, n)) continue;
      seen.add(n.id);
      out.push(n);
    }
  }
  return out;
}

/** Synergy passives on current kit that pair with spellId (for settled pick display). */
function synergiesEnabledBySpell(spellId, loadout) {
  const out = [];
  const seen = new Set();
  for (const hostId of Object.values(loadout || {})) {
    if (!hostId) continue;
    for (const n of synergiesHostedBy(hostId)) {
      if (n.requiresSpell !== spellId || seen.has(n.id)) continue;
      seen.add(n.id);
      out.push(n);
    }
  }
  return out;
}

function draftSynergyEnableHTML(partnerId) {
  const ps = SPELLS[partnerId];
  if (!ps) return "";
  return `<div class="draft-pick-meta draft-synergy-enabled">
    <span class="draft-synergy-enabled-label">Unlocked by your new skill</span>
    <span class="draft-synergy-enabled-partner">
      <span class="draft-host-icon" style="background:${ps.color}33;border:1px solid ${ps.color}">${spellIconInner(partnerId)}</span>
      ${escapeHtml(ps.name)}
    </span>
  </div>`;
}

function draftSpellSynergyHintHTML(spellId, loadout, committed, settled) {
  const syn = settled
    ? synergiesEnabledBySpell(spellId, loadout)
    : synergiesUnlockedBySpell(spellId, loadout, committed);
  if (!syn.length) return "";
  const names = syn.map(n => escapeHtml(n.name)).join(", ");
  return `<div class="draft-spell-synergy-hint">Unlocks synergy: <b>${names}</b></div>`;
}

/** Compact stat chips for a spell draft pick (mirrors spellTipHTML). */
function spellStatChipsHTML(id, lvl = 1) {
  const chips = spellStatsForTip(id, lvl);
  if (!chips.length) return "";
  return `<div class="draft-pick-stats">${chips.map(([k, v]) =>
    `<span class="draft-stat"><span class="draft-stat-k">${escapeHtml(k)}</span><span class="draft-stat-v">${escapeHtml(String(v))}</span></span>`
  ).join("")}</div>`;
}

/** Step key for deduping draft animation vs server updates. */
function draftStepKey(msg) {
  return `${msg.round}-${msg.stepIndex}-${msg.kind}`;
}

function ensureDraftLineLayer() {
  let svg = document.getElementById("draftPickLines");
  if (!svg) {
    const draft = document.getElementById("draft");
    svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.id = "draftPickLines";
    svg.className = "draft-pick-lines";
    svg.setAttribute("aria-hidden", "true");
    draft?.appendChild(svg);
  }
  return svg;
}

function clearDraftLineLayer() {
  const svg = document.getElementById("draftPickLines");
  if (svg) svg.innerHTML = "";
}

/** After pick animation: dim unchosen cards, keep selection highlighted. */
function settleDraftPickCards(cards, pickedBlock) {
  if (!cards) return;
  cards.classList.remove("draft-picks-locked");
  cards.classList.add("draft-picks-settled");
  cards.querySelectorAll(".draft-pick-block").forEach(el => {
    el.classList.remove("pickable");
    el.onclick = null;
    if (el === pickedBlock) {
      el.classList.add("selected", "owned-reveal");
    } else {
      el.classList.add("draft-unpicked");
    }
  });
}

/** Spell bar slot center in viewport coords (matches drawSpellBar layout). */
Game.prototype.draftSpellBarAnchor = function (opt) {
  const p = this.human();
  if (!p) return { x: this.W * 0.5, y: this.H - 50 };

  const lay = this.spellBarLayout();
  const spellId = opt.kind === "spell" ? opt.id : (opt.spellId || opt.spellID);
  const loadout = this.human()?.loadout || {};
  let targetSlot = opt.kind === "spell" ? nextFreeSlot(loadout) : null;
  if (!targetSlot) return { x: this.W * 0.5, y: this.H - 50 };

  if (targetSlot === lay.spaceSlot) {
    return { x: lay.spaceBoxX + lay.size * 0.5, y: lay.ySpace + lay.size * 0.5 };
  }

  const isPrimary = "1234".includes(targetSlot);
  const rowSlots = isPrimary ? lay.primarySlots : lay.secondarySlots;
  const slotIdx = rowSlots.indexOf(targetSlot);
  if (slotIdx < 0) return { x: this.W * 0.5, y: this.H - 50 };

  const x0 = isPrimary ? lay.primaryX : lay.secondaryX;
  const y0 = isPrimary ? lay.yTop : lay.yBottom;
  return { x: x0 + slotIdx * (lay.size + lay.gap) + lay.size * 0.5, y: y0 + lay.size * 0.5 };
};

Game.prototype.beginDraftPickAnim = function (block, opt, msg) {
  const stepKey = draftStepKey(msg);
  const cards = document.getElementById("draftCards");
  if (cards) cards.classList.add("draft-picks-locked");

  block.classList.remove("pickable");
  block.onclick = null;
  this.updateDraftRerollBtn({ kind: msg.kind, picked: "", rerollsLeft: 0, options: msg.options });
  Array.from(cards?.querySelectorAll(".draft-pick-block.pickable") || []).forEach(el => {
    el.classList.remove("pickable");
    el.onclick = null;
  });

  this._draftPickAnim = { stepKey, pickId: opt.id, animating: true };
  if (this.net) this.net.send({ t: "draftpick", id: opt.id });

  const rect = block.getBoundingClientRect();
  const toX = rect.left + rect.width * 0.5;
  const toY = rect.top + 2;
  const from = this.draftSpellBarAnchor(opt);
  const lineSvg = ensureDraftLineLayer();

  runPickUnlockAnim(block, lineSvg, from.x, from.y, toX, toY, () => {
    if (this._draftPickAnim?.stepKey !== stepKey || this._draftPickAnim?.pickId !== opt.id) {
      this.flushDraftQueued();
      return;
    }
    settleDraftPickCards(cards, block);
    clearDraftLineLayer();
    this._draftPickAnim = null;
    this.flushDraftQueued();
  }, DRAFT_PICK_UNLOCK_TIMING);
};

Game.prototype.shouldDeferDraftStep = function (msg) {
  const anim = this._draftPickAnim;
  if (!anim?.animating) return false;
  if (msg.kind === "done") return false;
  return draftStepKey(msg) !== anim.stepKey;
};

Game.prototype.flushDraftQueued = function () {
  if (this._draftPickAnim?.animating) return;
  const msg = this._draftPickQueued;
  if (!msg) return;
  this._draftPickQueued = null;
  this.onDraft(msg);
};

Game.prototype.renderDraftCards = function (msg, opts = {}) {
  clearDraftLineLayer();
  if (opts.clearPreview !== false) {
    window.SpellPreview?.hide?.();
  }
  const cards = document.getElementById("draftCards");
  const done = msg.kind === "done";
  const picked = msg.picked || "";
  const canPick = !done && !picked;
  if (cards) {
    cards.classList.remove("draft-picks-locked", "draft-picks-step-out");
    cards.classList.toggle("draft-picks-settled", !!(picked || done));
    cards.innerHTML = "";
    (msg.options || []).forEach((o, i) => {
      const block = this.draftPickBlock(o, i, msg);
      if (picked && o.id === picked) block.classList.add("selected", "owned-reveal");
      else if (picked) block.classList.add("draft-unpicked");
      if (canPick) {
        block.classList.add("pickable");
        block.onclick = () => {
          if (this._draftPickAnim?.animating) return;
          Sound.ui();
          this.beginDraftPickAnim(block, o, msg);
        };
      }
      cards.appendChild(block);
    });
    if (!done && !picked && (!msg.options || msg.options.length === 0)) {
      const skip = document.createElement("p");
      skip.className = "lobby-hint draft-pick-empty";
      skip.textContent = "Nothing to pick this step — advancing…";
      cards.appendChild(skip);
    }
  }
};

Game.prototype.renderDraft = function (msg) {
  const stepEl = document.getElementById("draftStepLabel");
  const progEl = document.getElementById("draftStepProgress");
  const done = msg.kind === "done";
  const stepKey = draftStepKey(msg);

  if (stepEl) {
    if (done) {
      stepEl.textContent = this.matchMode === "pve" && msg.round === 0
        ? "Draft complete — Boss 1 awaits…"
        : "Draft complete — starting next round…";
    } else if (this.matchMode === "pve" && msg.round === 0) {
      stepEl.textContent = `Prepare for Boss 1 — ${msg.label || "Pick one"}`;
    } else if (this.matchMode === "pve" && msg.round > 0) {
      stepEl.textContent = `Encounter ${msg.round} complete — ${msg.label || "Pick one"}`;
    } else {
      stepEl.textContent = msg.label || "Pick one";
    }
  }
  if (progEl && !done && msg.stepTotal > 0) {
    progEl.textContent = `Step ${(msg.stepIndex || 0) + 1} of ${msg.stepTotal}`;
    progEl.style.display = "";
  } else if (progEl) {
    progEl.style.display = "none";
  }

  this.updateDraftRerollBtn(msg);

  if (this._draftPickAnim?.animating && this._draftPickAnim?.stepKey === stepKey) {
    return;
  }

  const stepChanged = !!this._draftStepKey && this._draftStepKey !== stepKey && !done;
  const clearPreview = !this._draftStepKey || stepChanged;
  const cards = document.getElementById("draftCards");
  const hasCards = !!cards?.querySelector(".draft-pick-block");

  if (stepChanged && hasCards) {
    if (this._draftStepTransition) clearTimeout(this._draftStepTransition);
    cards.classList.add("draft-picks-step-out");
    this._draftStepTransition = setTimeout(() => {
      this._draftStepTransition = null;
      this._draftStepKey = stepKey;
      this.renderDraftCards(msg, { clearPreview: true });
    }, 180);
    return;
  }

  this._draftStepKey = stepKey;
  this.renderDraftCards(msg, { clearPreview });
  this.ensureDraftUi();
  this.layoutDraftOverlay();
};

/** Keep draft pick cards above the canvas spell bar (layout tracks resize). */
Game.prototype.layoutDraftOverlay = function () {
  const stack = document.querySelector(".draft-stack");
  if (!stack) return;
  const lay = this.spellBarLayout();
  const gap = 24;
  const topReserve = 88;
  const bottom = this.H - lay.yTop + gap;
  stack.style.bottom = `${bottom}px`;
  stack.style.maxHeight = `${Math.max(160, this.H - bottom - topReserve)}px`;
};

Game.prototype.ensureDraftUi = function () {
  if (this._draftUiWired) return;
  this._draftUiWired = true;
  const btn = document.getElementById("draftRerollBtn");
  if (btn) {
    btn.onclick = () => {
      if (this._draftPickAnim?.animating) return;
      Sound.ui();
      if (this.net) this.net.send({ t: "draftreroll" });
    };
  }
};

Game.prototype.updateDraftRerollBtn = function (msg) {
  this.ensureDraftUi();
  const btn = document.getElementById("draftRerollBtn");
  if (!btn) return;
  const done = msg.kind === "done";
  const picked = msg.picked || "";
  const left = msg.rerollsLeft || 0;
  const show = !done && !picked && left > 0 && (msg.options?.length > 0);
  btn.classList.toggle("hidden", !show);
  btn.disabled = !show || !!this._draftPickAnim?.animating;
  btn.textContent = left === 1 ? "Reroll options (1 left)" : `Reroll options (${left} left)`;
};

Game.prototype.draftPickBlock = function (o, i, msg) {
  const block = document.createElement("div");
  block.className = "draft-pick-block " + o.kind;
  block.style.animationDelay = `${i * 0.05}s`;
  if (o.kind === "spell") {
    return this.draftSpellBlock(block, o, msg);
  }
  return this.draftPassiveBlock(block, o, msg);
};

Game.prototype.draftSpellBlock = function (block, o, msg) {
  const s = SPELLS[o.id];
  if (!s) return block;
  const cat = o.shopCategory || s.shopCategory;
  const color = s.color;
  block.setAttribute("data-tip-kind", "spell");
  block.setAttribute("data-tip-id", o.id);
  if (window.SpellPreview?.has?.(o.id)) {
    block.classList.add("draft-pick-previewable");
    block.setAttribute("data-spell-preview", o.id);
  }
  const loadout = msg?.loadout || {};
  const committed = msg?.committed || {};
  const settled = (msg?.picked || "") === o.id;
  const desc = spellDescShort(o.id) || s.desc || "";
  block.innerHTML = `
    <div class="draft-slot-head">
      <span class="k">${escapeHtml(shopCategoryLabel(cat))}</span>
    </div>
    <div class="draft-pick-body">
      <div class="draft-pick-icon" style="background:${color}33;border:1px solid ${color}">${spellIconInner(o.id)}</div>
      <div class="draft-pick-name">${escapeHtml(s.name)}</div>
      ${desc ? `<div class="draft-pick-desc">${escapeHtml(desc)}</div>` : ""}
      ${spellStatChipsHTML(o.id, 1)}
      ${draftSpellSynergyHintHTML(o.id, loadout, committed, settled)}
    </div>`;
  return block;
};

Game.prototype.draftPassiveBlock = function (block, o, msg) {
  const spellId = o.spellId || o.spellID;
  const tree = PASSIVE_TREES[spellId] || [];
  const node = tree.find(n => n.id === o.id);
  const host = SPELLS[spellId];
  const color = host?.color || "#8a86a6";
  const isSyn = !!(node && node.synergy);
  if (isSyn) block.classList.add("synergy");
  const priorSpell = msg?.priorSpellPick || "";
  const newlyEnabled = isSyn && priorSpell && node?.requiresSpell === priorSpell;
  if (newlyEnabled) block.classList.add("synergy-newly-enabled");
  block.setAttribute("data-tip-kind", "passive");
  block.setAttribute("data-tip-id", o.id);
  block.setAttribute("data-tip-spell", spellId);
  if (isSyn && window.SpellPreview?.has?.(spellId)) {
    block.classList.add("draft-pick-previewable");
    block.setAttribute("data-synergy-preview", o.id);
  }
  const headLabel = isSyn ? "Synergy" : "Passive";
  const hostLine = host
    ? `<span class="draft-host-line"><span class="draft-host-icon" style="background:${color}33;border:1px solid ${color}">${spellIconInner(spellId)}</span>${escapeHtml(host.name)}</span>`
    : "";
  const partner = !newlyEnabled && node?.requiresSpell && SPELLS[node.requiresSpell]
    ? `<div class="draft-pick-meta">Pairs with <b>${escapeHtml(SPELLS[node.requiresSpell].name)}</b></div>`
    : "";
  const partnerEnabled = newlyEnabled ? draftSynergyEnableHTML(priorSpell) : "";
  const req = node ? passiveRequiresLabel(node, spellId) : "";
  const reqLine = req ? `<div class="draft-pick-req">${escapeHtml(req)}</div>` : "";
  const effect = node?.desc || "";
  const spellDesc = spellId ? spellDescShort(spellId) : "";
  const spellDescLine = spellDesc
    ? `<div class="draft-pick-desc">${escapeHtml(spellDesc)}</div>`
    : "";
  const orbContent = isSyn && node ? synergyOrbInner(node.id, node.name) : "";
  block.innerHTML = `
    <div class="draft-slot-head passive-head">
      <div class="passive-node-orb" aria-hidden="true">${orbContent}</div>
      <span class="slot-label">${headLabel}</span>
    </div>
    <div class="draft-pick-body">
      ${hostLine}
      ${spellDescLine}
      <div class="draft-pick-name">${escapeHtml(node?.name || o.id)}</div>
      <div class="draft-pick-effect">${escapeHtml(effect)}</div>
      ${partnerEnabled}${partner}${reqLine}
    </div>`;
  return block;
};

Game.prototype.draftCanPickNow = function () {
  const sandboxDraft = this.matchMode === "sandbox" && this.state === "playing" && this.sandboxDraftOpen;
  if ((!sandboxDraft && (this.matchMode !== "skilldraft" && this.matchMode !== "pve")) ||
    (!sandboxDraft && this.state !== "shop") || this.spectating) return false;
  if (this._draftPickAnim?.animating) return false;
  return !!document.querySelector("#draftCards .draft-pick-block.pickable");
};

Game.prototype.syncDraftTimerUrgency = function (left) {
  const el = document.getElementById("draftTimer");
  if (!el) return;
  const urgent = left != null && left <= 10 && left > 0;
  const critical = left != null && left <= 3 && left > 0;
  el.classList.toggle("draft-timer-urgent", urgent);
  el.classList.toggle("draft-timer-critical", critical);
};

Game.prototype.animateDraftTimer = function (critical) {
  const el = document.getElementById("draftTimer");
  if (!el) return;
  const cls = critical ? "draft-timer-tick-fast" : "draft-timer-tick";
  const animName = critical ? "draftTimerTickFast" : "draftTimerTick";
  el.classList.remove("draft-timer-tick", "draft-timer-tick-fast");
  void el.offsetWidth;
  el.classList.add(cls);
  const done = (e) => {
    if (e && (e.target !== el || e.animationName !== animName)) return;
    el.classList.remove(cls);
    el.removeEventListener("animationend", done);
  };
  el.addEventListener("animationend", done);
};

Game.prototype.tickDraftPickCountdown = function () {
  const sandboxDraft = this.matchMode === "sandbox" && this.state === "playing" && this.sandboxDraftOpen;
  const active = (sandboxDraft ||
    ((this.matchMode === "skilldraft" || this.matchMode === "pve") && this.state === "shop")) &&
    this.mode === "remote" && this.shopDeadline && !this.spectating && !this._drainPaused;
  if (!active) {
    this._draftPickCountN = 0;
    this.syncDraftTimerUrgency(null);
    return;
  }
  const left = Math.max(0, Math.ceil((this.shopDeadline - performance.now()) / 1000));
  if (left > 10) {
    this._draftPickCountN = 0;
    this.syncDraftTimerUrgency(left);
    return;
  }
  this.syncDraftTimerUrgency(left);
  if (left === 0 || !this.draftCanPickNow()) return;
  if (left !== this._draftPickCountN) {
    this._draftPickCountN = left;
    Sound.draftPickTick(left);
    this.animateDraftTimer(left <= 3);
  }
};

Game.prototype.onDraft = function (msg) {
  if (this.shouldDeferDraftStep(msg)) {
    this._draftPickQueued = msg;
    return;
  }
  if (this._draftPickQueued) this._draftPickQueued = null;
  if (this._draftPickAnim && draftStepKey(msg) !== this._draftPickAnim.stepKey) {
    this._draftPickAnim = null;
  }
  if (this.spectating) {
    this.onSpectatorShop({ round: msg.round, secs: msg.secs });
    return;
  }
  if (this.matchMode === "sandbox" && this.state === "playing") {
    this.openSandboxDraft(msg);
    return;
  }
  this.clearMatchmakerOnMatchStart();
  this.state = "shop";
  this.snaps = [];
  this._interpSnapRecv = 0;
  this.syncMusic();
  if (this.lobbyMode === "pve") this.matchMode = "pve";
  else this.matchMode = "skilldraft";
  this.shopReport = msg.report || null;
  const h = this.human();
  if (h) {
    if (msg.loadout) h.loadout = msg.loadout;
    if (msg.passives) h.passives = msg.passives;
    if (msg.committed) h.committed = msg.committed;
  }
  this.setShopDeadlineFromSecs(msg.secs);
  this.shopTotal = msg.total || 1;
  if (msg.kind === "done") {
    this.shopReadied = true;
    this.shopReadyCount = msg.total || 1;
    if (this.net) this.net.send({ t: "ready", on: true });
    this.updateReadyButton();
  } else {
    this.shopReadied = false;
    this.shopReadyCount = 0;
  }
  if (msg.kind === "done") this._draftPickAnim = null;
  window.SpellPreview?.hide?.();
  this.renderDraft(msg);
  this.hideAllPanels();
  this.show("draft");
  this.layoutDraftOverlay();
  if (msg.kind !== "done") Sound.ui();
};
